"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/Layout";
import PdfFormViewer from '../../components/PdfFormViewer';
import currencyConfig from '../../utils/currencyConfig';
import { dispatchCartEvent, CART_EVENTS } from '../../utils/cartEvents';
import { offeringsAPI } from '../../services/api';

const Basket = () => {
  const navigate = useNavigate();
  
  // Custom SVG Icons for content only
  const HomeIcon = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="currentColor"
      style={{ cursor: "pointer" }}
      onClick={() => navigate("/home")}
    >
      <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
    </svg>
  );
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // const [previewImg, setPreviewImg] = useState(null);
  const [preview, setPreview] = useState(null); // { type: 'image'|'pdf', src, name }
  const [previewLoadingId, setPreviewLoadingId] = useState(null);

  useEffect(() => { fetchCartItems(); }, []);

  useEffect(() => {
    if (!preview) return;
    const handleKey = (e) => { if (e.key === 'Escape') closePreview(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [preview]);

  const fetchCartItems = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('jwtToken');
      if (!token) { navigate('/login'); return; }
      
      // Use API service which includes browser_id automatically
      const response = await offeringsAPI.getBasket();
      setCartItems(response.data || []);
    } catch (error) { 
      console.error('Error fetching cart items:', error);
    } finally { 
      setIsLoading(false); 
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      // Find the item to check if it has an uploaded file
      const itemToRemove = cartItems.find(item => item.id === itemId);
      
      // Remove from cart using API service (includes browser_id automatically)
      const response = await offeringsAPI.removeFromBasket(itemId);
      
      if (response.success) { 
        // If the item has an uploaded file, delete it too
        if (itemToRemove?.form_data?.uploaded_file_id) {
          try {
            const token = localStorage.getItem('jwtToken');
            await fetch(`/api/uploads/files/${itemToRemove.form_data.uploaded_file_id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            });
          } catch (uploadError) {
            console.error('Error deleting uploaded file:', uploadError);
            // Don't fail the cart removal if upload deletion fails
          }
        }
        
        const updatedItems = cartItems.filter(item => item.id !== itemId);
        setCartItems(updatedItems); 
        toast.success('Item removed from basket');
        
        // Dispatch cart event for real-time updates
        dispatchCartEvent(CART_EVENTS.ITEM_REMOVED, { itemId, count: updatedItems.length });
        dispatchCartEvent(CART_EVENTS.CART_UPDATED, { count: updatedItems.length });
      }
      else { toast.error(response.message || 'Failed to remove item'); }
    } catch (error) { console.error('Error removing item:', error); toast.error('Error removing item'); }
  };

  const calculateTotal = () => cartItems.reduce((total, item) => total + parseFloat(item.amount || 0), 0).toFixed(2);

  const styles = {
    breadcrumbContent: { display:'flex', alignItems:'center', gap:8,},
    pageWrapper: {
        maxWidth:'100%',
        padding:'60px 20px',
    },
    tableOuter: {
      background:'#fff', borderRadius:8, boxShadow:'0 2px 4px rgba(0,0,0,0.05)', padding:'0 0 10px', border:'1px solid #e5e7eb' },
    table: { width:'100%', 
    
      borderCollapse:'collapse', fontSize:'0.85rem' },
    th: { background:'#f8f9fa', fontWeight:600, fontSize:'0.7rem', letterSpacing:'.5px', color:'#374151', textTransform:'uppercase', padding:'14px 10px', borderBottom:'1px solid #e5e7eb', textAlign:'center' },
    td: { padding:'14px 10px', borderBottom:'1px solid #f1f2f3', fontSize:'0.8rem', color:'#374151', textAlign:'center', verticalAlign:'middle' },
    refCell: { fontWeight:500 },
    formName: { textAlign:'left', fontSize:'0.8rem', fontWeight:500 },
    previewBtn: { background:'none', border:'none', cursor:'pointer', fontSize:'1rem', lineHeight:1, color:'#111827' },
    removeBtnIcon: { background:'none', border:'none', cursor:'pointer', fontSize:'1rem', color:'#111827' },
    amount: { fontWeight:500 },
    totalRowLabel: { textAlign:'right', fontWeight:600, paddingRight:20, fontSize:'0.75rem', color:'#111827' },
    actionBar: { display:'flex', justifyContent:'flex-end', gap:12, padding:'10px 20px 0' },
    requestMoreBtn: { background:'#4b5563', color:'#fff', fontSize:'0.75rem', border:'none', padding:'10px 30px', borderRadius:3, cursor:'pointer' },
    checkoutBtn: { background:'#2e9b31', color:'#fff', fontSize:'0.75rem', border:'none', padding:'10px 30px', borderRadius:3, cursor:'pointer' },
    emptyState: { background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:50, textAlign:'center', fontSize:'0.9rem', color:'#555', boxShadow:'0 2px 4px rgba(0,0,0,0.05)' },
    modalOverlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:20 },
    modalContent: { background:'#fff', borderRadius:10, maxWidth:'900px', width:'100%', maxHeight:'90vh', overflow:'auto', position:'relative', boxShadow:'0 10px 25px rgba(0,0,0,0.3)', padding:20 },
    modalClose: { position:'absolute', top:10, right:10, background:'#ef4444', color:'#fff', border:'none', borderRadius:4, padding:'6px 10px', cursor:'pointer', fontSize:'0.7rem' },
    modalImage: { width:'100%', height:'auto', display:'block', marginTop:20 }
  };

  const breadcrumb = (
    <div style={styles.breadcrumbContent}>
      <HomeIcon /><span>/</span><span style={{ cursor: "pointer" }} onClick={() => navigate("/home")}>Home</span><span>/</span><span>My Basket</span>
    </div>
  );

  const handlePreviewUploaded = async (item) => {
    if (!item?.form_data?.uploaded_file_id) return;
    try {
      setPreviewLoadingId(item.id);
      const token = localStorage.getItem('temple_token') || localStorage.getItem('jwtToken');
      const fileId = item.form_data.uploaded_file_id;
      const response = await fetch(`/api/uploads/files/${fileId}/view`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        console.error('Failed to fetch uploaded file');
        return;
      }

      // Backend now proxies the file - returns binary data (no CORS!)
      const blob = await response.blob();
      const contentType = response.headers.get('Content-Type') || 'application/pdf';
      
      // Create a URL from the blob
      const blobUrl = URL.createObjectURL(blob);
      const filename = item.form_data.original_filename || item.offering_type_name || 'Uploaded Form';

      if (contentType.includes('pdf')) {
        setPreview({ type: 'pdf', src: blobUrl, name: filename });
      } else if (contentType.includes('image')) {
        setPreview({ type: 'image', src: blobUrl, name: filename });
      } else {
        // Fallback: open in new tab
        window.open(blobUrl, '_blank');
      }
    } catch (e) {
      console.error('Preview uploaded file error:', e);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const closePreview = () => {
    if (preview?.type !== 'image' && preview?.src?.startsWith('blob:')) {
      try { URL.revokeObjectURL(preview.src); } catch {}
    }
    setPreview(null);
  };

  // Example preview logic for basket item
  const renderPreview = (form_img) => {
    if (form_img && form_img.startsWith('data:image')) {
      return <img src={form_img} alt="Form Preview" style={{ width: '100%', maxWidth: 400, border: '1px solid #ccc', borderRadius: 6 }} />;
    } else if (form_img && (form_img.startsWith('JVBER') || form_img.startsWith('0M8M'))) {
      // PDF base64 (starts with PDF magic number)
      return (
        <PdfFormViewer
          pdfFile={`data:application/pdf;base64,${form_img}`}
          width="100%"
          height={500}
        />
      );
    } else if (form_img) {
      // If form_img is base64 but not image, assume PDF
      return (
        <PdfFormViewer
          pdfFile={`data:application/pdf;base64,${form_img}`}
          width="100%"
          height={500}
        />
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <Layout activeMenuItem="My Basket" breadcrumb={breadcrumb}>
        <div style={styles.pageWrapper}><div style={styles.emptyState}>Loading your basket...</div></div>
      </Layout>
    );
  }

  return (
    <Layout activeMenuItem="My Basket" breadcrumb={breadcrumb}>
      <div style={styles.pageWrapper}>
        {cartItems.length === 0 ? (
          <div style={styles.emptyState}>Your basket is empty.</div>
        ) : (
          <div style={styles.tableOuter}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Ref.No</th>
                  <th style={{...styles.th, textAlign:'left'}}>Form Name</th>
                  <th style={styles.th}>Form</th>
                  <th style={styles.th}>Action</th>
                  <th style={styles.th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {cartItems.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td style={{...styles.td, ...styles.refCell}}>{idx + 1}</td>
                    <td style={{...styles.td, ...styles.formName}}>{item.request_type || item.offering_type || item.offering_type_name || 'Unknown Item'}{item.form_data?.uploaded_file_id ? ' (Uploaded)' : ''}</td>
                    <td style={styles.td}>
                      {(() => {
                        // Check if this is an uploaded file (form_data.uploaded_file_id or form_img is JSON with file_id)
                        let isUploadedFile = false;
                        
                        // Check form_data first
                        if (item.form_data?.uploaded_file_id) {
                          isUploadedFile = true;
                        }
                        
                        // Also check if form_img is JSON with file_id
                        if (!isUploadedFile && item.form_img && item.form_img.startsWith('{')) {
                          try {
                            const formImgData = JSON.parse(item.form_img);
                            if (formImgData.file_id || formImgData.file_path) {
                              isUploadedFile = true;
                            }
                          } catch (e) {
                            // Not JSON, continue
                          }
                        }
                        
                        // If uploaded file, use backend proxy endpoint (avoids CORS issues)
                        if (isUploadedFile) {
                          return (
                            <button
                              style={styles.previewBtn}
                              title="Preview uploaded file"
                              onClick={() => handlePreviewUploaded(item)}
                              disabled={previewLoadingId === item.id}
                            >{previewLoadingId === item.id ? '…' : '🔍'}</button>
                          );
                        }
                        
                        // Base64 encoded form (from PDF editor) or S3 URL
                        if (item.form_img) {
                          return (
                            <button
                              style={styles.previewBtn}
                              title="Preview form"
                              onClick={async () => {
                                // Handle S3 URLs (browser-filled forms)
                                if (item.form_img.startsWith('https://') || item.form_img.startsWith('http://')) {
                                  console.log('[Basket] Detected S3 URL, using backend proxy:', item.form_img);
                                  try {
                                    setPreviewLoadingId(item.id);

                                    // Extract S3 key from URL
                                    const urlParts = item.form_img.split('.amazonaws.com/');
                                    if (urlParts.length < 2) {
                                      throw new Error('Invalid S3 URL format');
                                    }
                                    const s3Key = urlParts[1];
                                    console.log('[Basket] Extracted S3 key:', s3Key);

                                    // Use backend proxy endpoint
                                    const token = localStorage.getItem('temple_token') || localStorage.getItem('jwtToken');
                                    const proxyUrl = `/api/uploads/s3-proxy/${s3Key}`;
                                    console.log('[Basket] Fetching via proxy:', proxyUrl);

                                    const response = await fetch(proxyUrl, {
                                      headers: {
                                        Authorization: `Bearer ${token}`
                                      }
                                    });

                                    if (!response.ok) {
                                      throw new Error(`Failed to fetch from proxy: ${response.status} ${response.statusText}`);
                                    }

                                    const blob = await response.blob();
                                    console.log('[Basket] Blob received via proxy, type:', blob.type, 'size:', blob.size);

                                    // Create blob URL
                                    const blobUrl = URL.createObjectURL(blob);
                                    const isPdf = blob.type.includes('pdf') || item.form_img.includes('.pdf');

                                    setPreview({
                                      type: isPdf ? 'pdf' : 'image',
                                      src: blobUrl,
                                      name: item.offering_type_name || 'Form'
                                    });
                                    console.log('[Basket] S3 file loaded successfully via proxy, isPdf:', isPdf);
                                  } catch (s3Error) {
                                    console.error('[Basket] Failed to fetch S3 file:', s3Error);
                                    toast.error('Failed to load form preview');
                                  } finally {
                                    setPreviewLoadingId(null);
                                  }
                                  return;
                                }

                                // Detect PDF base64 (data:application/pdf or PDF magic number)
                                if (item.form_img.startsWith('data:application/pdf') || item.form_img.startsWith('JVBER')) {
                                  setPreview({ type: 'pdf', src: item.form_img.startsWith('data:application/pdf') ? item.form_img : `data:application/pdf;base64,${item.form_img}`, name: item.offering_type_name });
                                } else {
                                  setPreview({ type: 'image', src: item.form_img, name: item.offering_type_name });
                                }
                              }}
                              disabled={previewLoadingId === item.id}
                            >{previewLoadingId === item.id ? '…' : '🔍'}</button>
                          );
                        }
                        
                        return '—';
                      })()}
                    </td>
                    <td style={styles.td}>
                      <button style={styles.removeBtnIcon} title="Remove" onClick={() => removeFromCart(item.id)}>✖</button>
                    </td>
                    <td style={{...styles.td, ...styles.amount}}>{currencyConfig.format(item.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={styles.td} colSpan={3}></td>
                  <td style={{...styles.td, ...styles.totalRowLabel}}>Total Price :</td>
                  <td style={{...styles.td, ...styles.amount}}>{currencyConfig.format(calculateTotal())}</td>
                </tr>
              </tbody>
            </table>
            <div style={styles.actionBar}>
              <button style={styles.requestMoreBtn} onClick={() => navigate('/home')}>Request more</button>
              <button style={styles.checkoutBtn} onClick={() => navigate('/checkout-flow')}>Go to Checkout</button>
            </div>
          </div>
        )}
      </div>

      {preview && (
        <div style={styles.modalOverlay} onClick={closePreview}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={closePreview}>Close</button>
            {preview.type === 'image' ? (
              <img src={preview.src} alt={preview.name} style={styles.modalImage} />
            ) : (
              <div style={{marginTop: 20}}>
                <PdfFormViewer pdfFile={preview.src} width="100%" height="80vh" />
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Basket;
